import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import css from "./Home.module.css";

/**
 * The shell every home widget renders into (ADR 274): a label, one number worth
 * glancing at, and a way to the page that explains it.
 *
 * Widgets do not style themselves. A catalogue where each member invents its own
 * card is a catalogue that cannot be rearranged without looking broken, and
 * rearranging is the whole point of the stage this is building toward.
 */
export function WidgetCard({
  title, caption, value, unit, hint, loading, tone = "neutral", to, children,
}: {
  title: string;
  /** What the number below is *about*, when the widget's own name does not say it — a
   *  card scoped to one aisle needs to name that aisle above the percentage, or the
   *  percentage is unreadable until you have read past it. */
  caption?: ReactNode;
  /** `null` renders an em dash: "we asked and there is no answer" is a real
   *  state and must never be drawn as a zero. */
  value?: string | null;
  unit?: string;
  hint?: ReactNode;
  loading?: boolean;
  tone?: "neutral" | "good" | "warn" | "bad";
  to?: string;
  children?: ReactNode;
}) {
  const body = (
    <>
      <div className={css.cardTitle}>{title}</div>
      {caption && <div className={css.cardCaption}>{caption}</div>}
      {loading
        ? <span className={`skeleton ${css.cardSkeleton}`} aria-hidden />
        : value !== undefined && (
            <div className={`${css.cardValue} ${css[`tone_${tone}`]}`}>
              {value ?? "—"}
              {value != null && unit && <span className={css.cardUnit}>{unit}</span>}
            </div>
          )}
      {children}
      {hint && <div className={css.cardHint}>{hint}</div>}
    </>
  );

  return to
    ? <Link to={to} className={`${css.card} ${css.cardLink}`}>{body}</Link>
    : <article className={css.card}>{body}</article>;
}

/** A labelled proportion bar — the only chart primitive the overview needs.
 *  Segments carry their own colour token; the legend names each one, because a
 *  bar read by colour alone says nothing to a third of readers. */
export function SegmentBar({ segments, ariaLabel }: {
  segments: { key: string; label: string; value: number; token: string }[];
  ariaLabel: string;
}) {
  // Format on the UI language, never the browser default: a bare
  // toLocaleString() rendered 13035 as "13,035", which in pt-BR reads as
  // thirteen-point-oh-three-five.
  const { i18n } = useTranslation();
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return null;
  return (
    <div className={css.segWrap}>
      <div className={css.segBar} role="img" aria-label={ariaLabel}>
        {segments.filter((s) => s.value > 0).map((s) => (
          <span key={s.key} className={css.seg}
                style={{ width: `${(s.value / total) * 100}%`, background: s.token }} />
        ))}
      </div>
      <ul className={css.segLegend}>
        {segments.map((s) => (
          <li key={s.key}>
            <span className={css.segDot} style={{ background: s.token }} aria-hidden="true" />
            {s.label} <b>{s.value.toLocaleString(i18n.language)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}
